import { useEffect, useState } from "react";
import { useDrop, DropTargetMonitor, useDragLayer, DragLayerMonitor } from "react-dnd";
import { IPositioningFlight, IFlight } from "@vmosolutions/oc-interfaces";
import { IPositioningFlightForm } from "./Forms/PositioningFlightForm";
import { Box, Theme } from "@mui/material";
import { convertFlightToPosFlight } from "./ConvertFlights";
// Redux Imports
import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IStructuresState } from "src/redux/reducers/StructuresReducer";

interface IPosFlightDropTargetProps {
  formProps: IPositioningFlightForm;
  loadAirportData: (icao: string) => void;
  children?: any;
}

export const PosFlightDropTarget = (props: IPosFlightDropTargetProps): JSX.Element => {
  // Set pending position flight.
  const { formProps, loadAirportData } = props;
  const [pendingFlight, setPendingFlight] = useState<IFlight>();
  const structures: IStructuresState = useSelector((state: IRootState) => state.structures);
  const { customerStructure } = structures;
  const iataCode = customerStructure?.globalAppConfig?.customer?.iataCode ?? undefined;

  // Monitor item being dragged, return as valid if type matches accepted.
  const { isValidDraggable } = useDragLayer((monitor: DragLayerMonitor) => {
    const draggedItem: any = monitor.getItem();
    return {
      isValidDraggable:
        monitor.isDragging() && (draggedItem?.type === "FLIGHT" || draggedItem?.type === "SELECTED_FLIGHTS"),
    };
  });

  // HANDLE FLIGHTS if dragged over pos flight form.
  const [{ isOver }, drop] = useDrop({
    accept: ["SELECTED_FLIGHTS", "FLIGHT"],
    drop(item: any, monitor: DropTargetMonitor) {
      let flight: IFlight | null = null;
      switch (item.type) {
        case "SELECTED_FLIGHTS":
          flight = item.selectedFlights[0];
          break;
        case "FLIGHT":
          flight = item.flight;
          break;
        default:
        // code block
      }
      const posFlight: IPositioningFlight = convertFlightToPosFlight(flight, iataCode);
      formProps.setValues(posFlight);
      // Load airport Data for origin and destination.
      loadAirportData(posFlight.origin);
      loadAirportData(posFlight.destination);
    },

    hover(item: any, monitor: DropTargetMonitor) {
      let flight: IFlight | null = null;
      switch (item.type) {
        case "SELECTED_FLIGHTS":
          flight = item.selectedFlights[0];
          break;
        case "FLIGHT":
          flight = item.flight;
          break;
        default:
        // code block
      }
      const hasChanged = flight?._id !== pendingFlight?._id;
      if (hasChanged) {
        setPendingFlight(flight);
      }
    },
    canDrop(item: any, monitor: DropTargetMonitor) {
      return (
        (item.type === "SELECTED_FLIGHTS" && item?.selectedFlights?.length === 1) ||
        (item.type === "FLIGHT" && item.flight)
      );
    },
    collect(monitor: DropTargetMonitor) {
      return {
        isOver: monitor.isOver(),
      };
    },
  });

  useEffect(() => {
    if (!isOver) setPendingFlight(undefined);
  }, [isOver]);

  const DropZoneStyles = (theme: Theme): any => ({
    event_dropzone: {
      display: "flex",
      cursor: "pointer",
      minHeight: "100px",
      justifyContent: "center",
      alignItems: "flex-start",
      flexDirection: "column",
      flexGrow: 1,
      backgroundColor: theme.palette.background.paper,
      borderRadius: "3px",
      padding: "4px",
      "& .title": { fontSize: 18, fontWeight: "bold" },
      "& .body": {
        display: "flex",
        border: `2x solid ${theme.custom.forms.inputBorder}`,
        minHeight: "40px",
        alignItems: "center",
        color: theme.custom.forms.focusedColor,
        fontSize: 12,
        fontWeight: 300,
        textAlign: "center",
        padding: "4px 8px",
        opacity: 0.5,
        userSelect: "none",
      },
      "&.disabled": {
        opacity: 0.5,
        userSelect: "none",
        pointerEvents: "none",
      },
      "&.reject": {
        backgroundcolor: theme.custom.forms.errorColor,
      },
      "&.active": {
        backgroundcolor: theme.custom.forms.focusedColor,
      },
      "&.dirty": {
        backgroundcolor: theme.custom.forms.dirtyColor,
      },
      "&.over": {
        backgroundcolor: theme.custom.forms.dirtyColor,
      },
    },
  });

  return (
    <Box
      className={`${isValidDraggable && "active"} ${isOver && "over"}`}
      ref={drop}
      sx={(theme) => DropZoneStyles(theme).event_dropzone}
    >
      <div className="body">
        {formProps?.values?.flight_id
          ? "To replace existing reference, drag/drop another flight into this zone from the timeline or advanced search menu."
          : "To reference an existing flight, drag/drop them into this zone from the timeline or advanced search menu."}
      </div>
      <div style={{ display: "flex", padding: 10 }}>{props?.children}</div>
    </Box>
  );
};
